import React,{useContext,useEffect, useState, useRef,useReducer,useMemo} from 'react';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { LocaleContext } from '../../../components/LocaleContext';
import { defaultProductLogoSrc } from '../../../constants/brandingDisplay';
import { buildCanonicalPrintFooterHtml, buildCanonicalPrintHeaderHtml } from '../../../utils/printChrome';
import { useFormik } from 'formik';
import { getRequest, postRequest,deleteRequest } from '../../../api/ApiController';
import { SaveIcon, AddIcon, CopyIcon, PrintIcon, VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon, SaveAndCloseIcon,EditRowIcon,PaperClipIcon,EditIcon,DeleteIcon, CollapseTreeIcon, ExpandTreeIcon, BranchTreeIcon} from "../../svg/svgIcons";
import { Dialog } from 'primereact/dialog';
import AuthContext from '../../../components/AuthContext';
import UtilContext from '../../../components/UtilContext';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { ReactTabulator,reactFormatter } from 'react-tabulator';
import { DateTime } from "luxon";
import { OverflowMenuProvider } from '../../../components/toolbar/OverflowMenu';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { PanelMenu } from 'primereact/panelmenu';
import { Checkbox } from 'primereact/checkbox';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';
import ReactDOMServer from 'react-dom/server';
import { createArrayNavigateHandlers } from '../../../utils/arrayNavigate';
import { resetTabulatorMask } from '../../../utils/tabulatorResetMask';
import { primeMultiSelectHeaderFilter } from '../../../utils/primeMultiSelectHeaderFilter';
import { getPusherChannel } from '../../../utils/pusherChannel';
import {
  appendAuditFieldsToColumnLayout,
  buildAuditTabulatorColumns,
  mergeColumnVisibilityWithAuditDefaults,
  TABULATOR_AUDIT_PROFILES,
} from '../../../utils/tabulatorAuditColumns';
import {
  isCompanyCertificateParentRow,
  resolveCardDeactivationDate,
  resolveCardResponsibleName,
  resolveCatalogDescription,
  resolveCertificateTreeAuditRow,
  resolveIssuanceDescription,
  resolveIssuanceEndOfValidity,
  resolveIssuanceFiles,
  resolveIssuanceStartOfValidity,
  resolveIssuanceStateLabel,
} from '../../../utils/companyCertificateCurrentRecord';

export const TRAINING_COMPANY_CERT_RECORD_DETAIL_DEFAULT_COLUMNS_VISIBLE = Object.freeze({
    name: true,
    description: true,
    calibration_company_id: true,
    custom_start_of_validity: true,
    custom_end_of_validity: true,
    cert_description: true,
    certificate_status: true,
    'responsible.name': true,
    protocol: true,
    state: true,
    created_at: true,
    updated_at: true,
});

/** Pôvodný mask dialog (index 2–6) plus auditné stĺpce; ostatné dátové polia ostávajú predvolene viditeľné. */
export const TRAINING_COMPANY_CERT_RECORD_DETAIL_MASK_DIALOG_FIELDS = Object.freeze([
    'name',
    'description',
    'custom_start_of_validity',
    'custom_end_of_validity',
    'cert_description',
    'created_at',
    'updated_at',
]);

export function isUsableCompanyCertProtocolFileId(file_id) {
    return file_id != null && file_id !== '';
}

export function companyCertRecordProtocolPath(file_id) {
    return `/api/company_cert_records/protocol/${file_id}`;
}

export function openCompanyCertRecordProtocol(file_id, { request, preview, onError } = {}) {
    if (!isUsableCompanyCertProtocolFileId(file_id)) {
        return Promise.resolve();
    }
    return request(companyCertRecordProtocolPath(file_id))
        .then((response) => {
            if (response?.data?.url) {
                preview?.({ url: response.data.url, filename: response.data.filename });
                return;
            }
            onError?.();
        })
        .catch(() => {
            onError?.();
        });
}

export const TRAINING_COMPANY_CERT_RECORD_DETAIL_PAGE_SIZE = 200;
export const TRAINING_COMPANY_CERT_RECORD_DETAIL_PAGED_ENDPOINT = '/api/company_certificates/paged';

export function isUsableTrainingCompanyId(companyId) {
    return companyId != null && companyId !== '';
}

export function buildTrainingCompanyCertRecordDetailPagedUrl(companyId, page = 1) {
    const params = new URLSearchParams();
    params.set('type', 'training');
    params.set('company_id', String(companyId));
    params.set('size', String(TRAINING_COMPANY_CERT_RECORD_DETAIL_PAGE_SIZE));
    params.set('page', String(page));
    return `${TRAINING_COMPANY_CERT_RECORD_DETAIL_PAGED_ENDPOINT}?${params.toString()}`;
}

export function parseTrainingCompanyCertRecordDetailPagedEnvelope(response) {
    if (response == null || typeof response !== 'object' || Array.isArray(response)) return null;
    if (typeof response.status === 'number' && response.status !== 200) return null;
    if (!Array.isArray(response.data)) return null;
    const lastPage = response.last_page == null ? 1 : Number(response.last_page);
    if (!Number.isFinite(lastPage) || lastPage < 1) return null;
    return { rows: response.data, lastPage };
}

export async function fetchTrainingCompanyCertRecordDetailPages(companyId, { request } = {}) {
    if (!isUsableTrainingCompanyId(companyId)) {
        return [];
    }
    const req = request;
    if (typeof req !== 'function') {
        throw new Error('missing_request');
    }
    const first = await req(buildTrainingCompanyCertRecordDetailPagedUrl(companyId, 1));
    const parsed = parseTrainingCompanyCertRecordDetailPagedEnvelope(first);
    if (parsed == null) {
        throw new Error('invalid_paged_envelope');
    }
    const rows = parsed.rows.slice();
    for (let page = 2; page <= parsed.lastPage; page += 1) {
        const next = await req(buildTrainingCompanyCertRecordDetailPagedUrl(companyId, page));
        const nextParsed = parseTrainingCompanyCertRecordDetailPagedEnvelope(next);
        if (nextParsed == null) {
            throw new Error('invalid_paged_envelope');
        }
        rows.push(...nextParsed.rows);
    }
    return rows;
}

export async function loadTrainingCompanyCertRecordDetailCertificates(companyId, { request, isCurrent, onSuccess } = {}) {
    const current = typeof isCurrent === 'function' ? isCurrent : () => true;
    if (!isUsableTrainingCompanyId(companyId)) {
        if (current()) onSuccess?.([]);
        return [];
    }
    try {
        const rows = await fetchTrainingCompanyCertRecordDetailPages(companyId, { request });
        if (!current()) return rows;
        onSuccess?.(rows);
        return rows;
    } catch (error) {
        if (current()) onSuccess?.([]);
        throw error;
    }
}

export default function TrainingCompanyCertRecordDetail({training_company, type}) {

    const [state,dispatch] = useContext(DatabaseContext);
    const { t } = useContext(LocaleContext);
    const {setDialogText,setVisible,openFavoriteDialog,toast,setFastView,setUserMaskFilter,openFilePreview,getDateFormat, getUserColor, getUserInitials, openChosenNotificationPreferences} = useContext(UtilContext);
    const {user} = useContext(AuthContext);
    const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
    const dateTimeFmt = `${dateFmt} HH:mm`;

    const [closeTab,setCloseTab] = useState(false);
    const ref = useRef(null);
    const submitRef = useRef(null);
    const overlayRef = useRef(null);

    const [formTrainingCompany,setformTrainingCompany] = useState(training_company);
    const [formType, setFormType] = useState(type);

    const [markUnmark, setMarkUnmarked] = useState(true);
    const [selectedRecord,setSelectedRecord] = useState(null);

    const [columnsVisible, setColumnsVisible] = useState(() => ({ ...TRAINING_COMPANY_CERT_RECORD_DETAIL_DEFAULT_COLUMNS_VISIBLE }));
    const [maskVisible, setMaskVisible] = useState(false);
//TODO dorobit contractor id
    const [scrollIcons,setScrollIcons] = useState((user.permissions?.includes("edit training-company"))?false:true);
    const [editIcon,setEditIcon] = useState(true);
    const [deleteIcon,setDeleteIcon] = useState(true);
    
    const [visibleRecord,setVisibleRecord] = useState(false);
    const [images,setImages] = useState('');
    const [updatefileList, setUpdateFileList] = useState([]);
    const updateFileListRef = useRef(null);
    const [company,setCompany] = useState("");
    const [certificates,setCertificates] = useState([]);
    const [trainingCertificates, setTrainingCertificates] = useState([]);
    const currentCompanyId = formTrainingCompany?.id ?? null;
    const currentCompanyIdRef = useRef(currentCompanyId);
    currentCompanyIdRef.current = currentCompanyId;
    const certificateLoadGenerationRef = useRef(0);

    function loadTrainingCertificates(companyId = currentCompanyIdRef.current) {
        const generation = certificateLoadGenerationRef.current + 1;
        certificateLoadGenerationRef.current = generation;
        return loadTrainingCompanyCertRecordDetailCertificates(companyId, {
            request: getRequest,
            isCurrent: () => generation === certificateLoadGenerationRef.current,
            onSuccess: setTrainingCertificates,
        }).catch(() => {});
    }

    useEffect(() => {
        loadTrainingCertificates(currentCompanyId);
    }, [currentCompanyId]);
    useEffect(() => {
      const channel = getPusherChannel();
      if (!channel) return;
      const DEDUPE_MS = 800;
      const lastHandled = { key: '', at: 0 };
      const shouldSkip = (eventType, id) => {
        if (id == null) return true;
        const key = `${eventType}-${id}`;
        const now = Date.now();
        if (lastHandled.key === key && now - lastHandled.at < DEDUPE_MS) return true;
        lastHandled.key = key;
        lastHandled.at = now;
        return false;
      };
      const listType = 'training';
      const isMyType = (p) => p?.type === listType;
      const onCreated = (payload) => {
        if (!isMyType(payload)) return;
        const id = payload?.id;
        if (id == null || shouldSkip('company_certificate.created', id)) return;
        getRequest(`/api/company_certificates/${id}`).then((res) => {
          const row = res?.data?.data ?? res?.data ?? res;
          if (!row?.id || row.company_id !== currentCompanyId) return;
          setTrainingCertificates((prev) => {
            const next = (prev ?? []).filter((c) => c.id !== row.id);
            return [...next, row];
          });
        }).catch(() => {});
      };
      const onUpdated = (payload) => {
        if (!isMyType(payload)) return;
        const id = payload?.id;
        if (id == null || shouldSkip('company_certificate.updated', id)) return;
        getRequest(`/api/company_certificates/${id}`).then((res) => {
          const row = res?.data?.data ?? res?.data ?? res;
          if (!row?.id) return;
          if (row.company_id === currentCompanyId) {
            setTrainingCertificates((prev) => {
              const next = (prev ?? []).filter((c) => c.id !== row.id);
              return [...next, row];
            });
          } else {
            setTrainingCertificates((prev) => (prev ?? []).filter((c) => c.id !== id));
          }
        }).catch(() => {});
      };
      const onDeleted = (payload) => {
        if (!isMyType(payload)) return;
        const id = payload?.id;
        if (id == null || shouldSkip('company_certificate.deleted', id)) return;
        setTrainingCertificates((prev) => (prev ?? []).filter((c) => c.id !== id));
      };
      const refetch = (payload) => {
        const id = payload?.id;
        if (id == null) return;
        getRequest(`/api/company_certificates/${id}`).then((res) => {
          const row = res?.data?.data ?? res?.data ?? res;
          if (!row?.id) return;
          setTrainingCertificates((prev) => {
            const next = (prev ?? []).filter((c) => c.id !== row.id);
            return [...next, row];
          });
        }).catch(() => {});
      };
      channel.bind('company_certificate.created', onCreated);
      channel.bind('company_certificate.updated', onUpdated);
      channel.bind('company_certificate.deleted', onDeleted);
      channel.bind('company_certificate.activated', refetch);
      channel.bind('company_certificate.deactivated', refetch);
      return () => {
        channel.unbind('company_certificate.created', onCreated);
        channel.unbind('company_certificate.updated', onUpdated);
        channel.unbind('company_certificate.deleted', onDeleted);
        channel.unbind('company_certificate.activated', refetch);
        channel.unbind('company_certificate.deactivated', refetch);
      };
    }, [currentCompanyId]);

    const handleUpdateFileChange = (event) => {
        setUpdateFileList(event.target.files);
    };

    const validate = values => {
        const errors = {};
        return errors;
    };

    const formikUpdate = useFormik({
        initialValues: selectedRecord==null?{
            start_of_validity: '',end_of_validity: '',description:'',certificate_id:''}
            :{
              id:selectedRecord?.id,
              start_of_validity:selectedRecord?.start_of_validity,end_of_validity:selectedRecord?.end_of_validity,
              description:selectedRecord?.description,certificate_id:selectedRecord?.certificate_id
            },
        enableReinitialize:true,
        validate,
        onSubmit: values => {
            let formData = new FormData();
            for (let value in values) {
                formData.append(value, values[value] != null ? values[value] : "");
            }
            /*for (var pair of formData.entries()) {
                console.log(pair[0]+ ', ' + pair[1]); 
            }*/
            for(let i = 0;i<updatefileList.length;i++){
              //console.log("IMAGE :"+fileList[i].name);
              formData.append('files[]',updatefileList[i]);
            }
            postRequest(`/api/company_cert_records/update/${formikUpdate.values.id}`, formData,true)
            .then((response) => {
                console.log(response);
                getRequest("/api/companies").then((response) => {
                    dispatch({type:'load_companies',newCompanies:response.data});
                    setVisibleRecord(false);
                    formikUpdate.resetForm();
                    if(updatefileList.length){
                        setUpdateFileList([]);
                        updateFileListRef.current.value = null;
                    }
                    toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('Common.Toast.RecordSavedDetail') });
                });
                loadTrainingCertificates();
            }).catch((reason) => {
                if(!navigator.online){
                    alert(t('ServiceTask.OfflineQueuedAlert'));
                }
                else{
                    alert(t('Common.Toast.ActionFailedDetail'));
                }
            })
        },
    });

    function hideCertRecord(){
      formikUpdate.resetForm();
      setVisibleRecord(false);
    }

    const { scrollToFirst, scrollUp, scrollDown, scrollToLast } = useMemo(
        () => createArrayNavigateHandlers(trainingCertificates, formTrainingCompany?.id, setformTrainingCompany),
        [trainingCertificates, formTrainingCompany?.id]
    );

    const onSelectRow =  (data, rows) => {
      setDeleteIcon(true);
      setEditIcon(true);
      formikUpdate.resetForm();
      if(data.length>0){
          let children = 0;
          let parent = 0;
          data.forEach(obj => {
              if(obj?.certificate_id>0){
                  children++;
              }
              else parent++;
          });
          if(children>0 && parent==0){
            console.log(user.permissions);
              if(user.permissions?.includes("delete training-company"))setDeleteIcon(false);
              if(data?.length==1 && user.permissions?.includes("edit training-company")){
                  setSelectedRecord(data[0]);
                  formikUpdate.setValues(data[0]);
                  setEditIcon(false);
              }
          }
      }
    }

    function getRecordFiles(id){
        getRequest(`/api/company_cert_records/protocols/${id}`).then((response) => {
            console.log(response);
            if(response.data!=="{}")setImages(response.data);
        });
    }
    
    const acceptFile = (id) => {
        getRequest(`/api/company_cert_records/deleteProtocol/${id}`)
        .then((response) => {
            toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('Common.Toast.FileDeletedDetail') });
            getRecordFiles(selectedRecord?.id);
        }).catch((reason) => {
            alert(reason);
            toast?.current?.show({ severity: 'warn', summary: t('Common.Rejected'), detail: t('Common.Toast.FileDeleteFailedDetail'), life: 3000 });
        })
    };
    

    function deleteFiles(img){
      confirmDialog({
          message: t('Common.ConfirmDeleteFile'),
          header: t('Common.ConfirmDelete'),
          icon: 'pi pi-info-circle',
          acceptClassName: 'p-button-danger',
          acceptLabel: t('Common.No'),
          rejectLabel: t('Common.Yes'),
          accept:reject,
          reject:() => acceptFile(img.id)
      });
    }

    function NameFormatter(props) {
      const rowData = props.cell.getRow().getData();
      var parent = props.cell.getRow()?.getTreeParent();
      if(rowData?._children) return <div className='training_name'>{rowData.name}</div>;
      if(parent){
          let parent_array = parent._row.data._children;
          for(let i = 0;i<parent_array.length;i++){
              if(parent_array[i].id == rowData.id){
                  if(i==parent_array.length-1)return <div className='training_name'>Prvotný certifikát</div>;
                  else return <div className='training_name'>{parent_array.length-1-i}. Opakovaný certifikát</div>;
              }
          }
          return <div className='training_name'>{parent._row.data.name}</div>;
      }
      return "";
  }  

    function viewFile(file_id){
        return openCompanyCertRecordProtocol(file_id, {
            request: getRequest,
            preview: openFilePreview,
            onError: () => {
                toast?.current?.show({
                    severity: 'warn',
                    summary: t('Common.Rejected'),
                    detail: t('Common.Toast.ActionFailedDetail'),
                    life: 3000,
                });
            },
        });
    }

      function Protocol(props) {
        const rowData = props.cell.getRow().getData();
        const files = resolveIssuanceFiles(rowData);
        if(files.length > 0) return <div className='flex flex-row'>{files.map((img) => (<a style={{color:'blue',cursor:'pointer',marginBottom:'2px',width: '24px',display: 'block'}} onClick={() => {viewFile(img.id);}}><PaperClipIcon/></a>))}</div>;
        return "";
    }

    function EndOfValidity(props){
        const rowData = props.cell.getRow().getData();
        const end = resolveIssuanceEndOfValidity(rowData);
        if(end) return DateTime.fromISO(end).toFormat(dateFmt);
        return <span className='text-red-500'>{t('Ui.R67.Necertifikovane')}</span>;
    }

    function StartOfValidity(props){
        const rowData = props.cell.getRow().getData();
        const start = resolveIssuanceStartOfValidity(rowData);
        if(start) return DateTime.fromISO(start).toFormat(dateFmt);
        return <span className='text-red-500'>{t('Ui.R67.Necertifikovane')}</span>;
    }

    function StateFormatter(props){
      return resolveIssuanceStateLabel(props.cell.getRow().getData());
    }

    function CatalogDescriptionFormatter(props){
        const rowData = props.cell.getRow().getData();
        return resolveCatalogDescription(rowData);
    }

    function CertDescriptionFormatter(props){
        const rowData = props.cell.getRow().getData();
        return resolveIssuanceDescription(rowData);
      }

    function getStateLabel(rowData) {
        return resolveIssuanceStateLabel(rowData);
    }

    function startOfValidityDisplayStr(rowData) {
        const start = resolveIssuanceStartOfValidity(rowData);
        if (start) return DateTime.fromISO(start).toFormat(dateFmt);
        return '';
    }
    function endOfValidityDisplayStr(rowData) {
        const end = resolveIssuanceEndOfValidity(rowData);
        if (end) return DateTime.fromISO(end).toFormat(dateFmt);
        return '';
    }

    function CertificateStatusFormatter(props) {
        const row = props.cell.getRow();
        const rowData = row.getData();
        const card = isCompanyCertificateParentRow(rowData)
          ? rowData
          : (row.getTreeParent()?._row?.data ?? row.getTreeParent()?.getData?.());
        const v = resolveCardDeactivationDate(card);
        return <span>{v ? t('Status.Deactivated') : 'Aktívny'}</span>;
    }
    function ResponsibleFormatter(props) {
        const row = props.cell.getRow();
        const rowData = row.getData();
        if (isCompanyCertificateParentRow(rowData)) {
          return <span>{resolveCardResponsibleName(rowData)}</span>;
        }
        const card = row.getTreeParent()?._row?.data ?? row.getTreeParent()?.getData?.();
        return <span>{resolveCardResponsibleName(card)}</span>;
    }

    const auditTailColumns = useMemo(
        () =>
            buildAuditTabulatorColumns({
                profile: TABULATOR_AUDIT_PROFILES.createdByUpdatedBy,
                includeId: false,
                dateFormat: dateFmt,
                dateTimeFormat: dateTimeFmt,
                getUserColor,
                getUserInitials,
                remotePassthrough: false,
                auditSourceFn: resolveCertificateTreeAuditRow,
            }),
        [dateFmt, dateTimeFmt, getUserColor, getUserInitials]
    );

    const [baseColumns] = useState([
      { title: '', field: 'id',visible:false},
      {
        formatter:"rowSelection", titleFormatter:"rowSelection",titleFormatterParams:{rowRange:"active"},hozAlign:"center",width: 44,minWidth: 44,headerSort:false,resizable:false,
        cellClick: function(e, cell) {
          cell.getRow().toggleSelect();
          console.log(cell.getRow(), cell.getRow().getTreeParent());
        }
      },
      { title: t('Common.Name'), field: 'name',formatter: reactFormatter(<NameFormatter/>),headerFilter:"input",minWidth: 200, widthGrow:1},
      { title: t('Common.Description'), field: 'description', formatter: reactFormatter(<CatalogDescriptionFormatter/>), headerFilter: 'input', headerFilterFunc: (headerValue, rowValue, rowData) => { if (!headerValue || String(headerValue).trim() === '') return true; const s = resolveCatalogDescription(rowData).toString().toLowerCase(); return s.includes(String(headerValue).trim().toLowerCase()); }, minWidth: 200, widthGrow:1},
      { title: t('Common.ValidFrom'), field: 'custom_start_of_validity', headerFilter: 'input', headerFilterFunc: (headerValue, rowValue, rowData) => { if (!headerValue || String(headerValue).trim() === '') return true; const s = startOfValidityDisplayStr(rowData).toLowerCase(); return s.includes(String(headerValue).trim().toLowerCase()); }, minWidth: 200, widthGrow: 1, formatter: reactFormatter(<StartOfValidity/>) },
      { title: t('Common.ValidTo'), field: 'custom_end_of_validity', headerFilter: 'input', headerFilterFunc: (headerValue, rowValue, rowData) => { if (!headerValue || String(headerValue).trim() === '') return true; const s = endOfValidityDisplayStr(rowData).toLowerCase(); return s.includes(String(headerValue).trim().toLowerCase()); }, minWidth: 200, widthGrow: 1, formatter: reactFormatter(<EndOfValidity/>) },
      { title: t('Common.CertificateDescription'), field: 'cert_description', formatter: reactFormatter(<CertDescriptionFormatter/>), headerFilter: 'input', headerFilterFunc: (headerValue, rowValue, rowData) => { if (!headerValue || String(headerValue).trim() === '') return true; const s = resolveIssuanceDescription(rowData).toString().toLowerCase(); return s.includes(String(headerValue).trim().toLowerCase()); }, minWidth: 200, widthGrow: 1 },
      { title: t('Common.CertificateStatus'), field: 'certificate_status', headerFilter: primeMultiSelectHeaderFilter, headerFilterParams: { options: [{ label: t('Status.Active'), value: 'Aktívny' }, { label: t('Status.Deactivated'), value: "Deaktivovaný" }], placeholder: t('Common.CertificateStatus') }, headerFilterFunc: (headerValue, rowValue, rowData) => { if (!isCompanyCertificateParentRow(rowData)) return true; if (!headerValue || String(headerValue).trim() === '') return true; const selectedRaw = Array.isArray(headerValue) ? headerValue : (typeof headerValue === 'object' && Array.isArray(headerValue?.value) ? headerValue.value : String(headerValue).split(/[\s,;|]+/)); const selected = selectedRaw.map(s => String(s).trim()).filter(Boolean); if (selected.length === 0) return true; const v = resolveCardDeactivationDate(rowData); const label = v ? t('Status.Deactivated') : 'Aktívny'; return selected.includes(label); }, minWidth: 200, formatter: reactFormatter(<CertificateStatusFormatter/>) },
      { title: t('Common.Column.Responsible'), field: 'responsible.name',formatter: reactFormatter(<ResponsibleFormatter/>),headerFilter:"input",minWidth: 260, widthGrow:1},
      { title: t('Common.Status'), field: 'state', headerFilter: primeMultiSelectHeaderFilter, headerFilterParams: { options: [{ label: 'OK', value: 'OK' }, { label: 'NOK', value: 'NOK' }], placeholder: t('Common.Status') }, headerFilterFunc: (headerValue, rowValue, rowData) => { if (!headerValue || String(headerValue).trim() === '') return true; const selectedRaw = Array.isArray(headerValue) ? headerValue : (typeof headerValue === 'object' && Array.isArray(headerValue?.value) ? headerValue.value : String(headerValue).split(/[\s,;|]+/)); const selected = selectedRaw.map(s => String(s).trim()).filter(Boolean); if (selected.length === 0) return true; const label = getStateLabel(rowData); return selected.includes(label); }, minWidth: 200, widthGrow: 1, formatter: reactFormatter(<StateFormatter/>) },
      { title: 'Protokol', field: 'protocol',headerFilter:"input",minWidth: 200, widthGrow:1,formatter: reactFormatter(<Protocol/>)},
    ]);
    const columns = useMemo(() => [...baseColumns, ...auditTailColumns], [baseColumns, auditTailColumns]);

    function setMask(item,value){
        const columnVisible = {...columnsVisible};
       columnVisible[item] = value;
       console.log(columnVisible);
        for(const val in columnVisible) {
          if(columnVisible[val]==false){
            ref.current.hideColumn(`${val}`);
          }
          else{
            ref.current.showColumn(`${val}`);
          }
        }
        setColumnsVisible(columnVisible);
      }

      const itemRenderer = (item, options) => (
        <a className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px]" onClick={options.onClick}>
            <span className={`${item.icon} text-primary`} />
            <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
            {item.shortcut && <span className="ml-auto border-1 surface-border border-round surface-100 text-xs p-1">{item.shortcut}</span>}
        </a>
      );
    
      const [expandedKeys, setExpandedKeys] = useState({});
    
      const handleClickOutside = (event) => {
        if (overlayRef.current && !overlayRef.current.contains(event.target)) {
            setExpandedKeys({});
        }
      };
    
      useEffect(() => {
        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    const defaultColumnsVisible = TRAINING_COMPANY_CERT_RECORD_DETAIL_DEFAULT_COLUMNS_VISIBLE;
    const resetMask = () => resetTabulatorMask({ page: 'TrainingCompanyCertRecordDetail', defaultColumnsVisible, ref, columns, setColumnsVisible });
  
      const items = [
      {
        key: '0',
        label: t('Common.Actions'),
        icon: 'pi pi-sliders-v',
        template: itemRenderer,
        items: [
            {
                key: '0_1',
                label: t('Common.SetFilter'),
                icon: 'pi pi-filter',
                command: () => {setUserMaskFilter("TrainingCompanyCertRecordDetail","set_filters",null,ref.current.getHeaderFilters(),null);},
                template: itemRenderer,
            },
              {
                key: '0_2',
                label: t('Common.ClearFilter'),
                icon: 'pi pi-filter-slash',
                command: () => {setUserMaskFilter("TrainingCompanyCertRecordDetail","set_filters",null,[],null);ref.current.clearHeaderFilter();},
                template: itemRenderer,
            },
            {
                key: '0_3',
                label: t('Common.SetSorting'),
                icon: 'pi pi-sort-alt',
                command: () => {setUserMaskFilter("TrainingCompanyCertRecordDetail","set_sorters",null,null,null,ref.current.getSorters());OverflowMenuProvider.setVisible(null);setExpandedKeys({});},
                template: itemRenderer,
            },
            {
                key: '0_4',
                label: t('Common.ClearSorting'),
                icon: 'pi pi-sort-alt-slash',
                command: () => {setUserMaskFilter("TrainingCompanyCertRecordDetail","set_sorters",null,null,null,[]);ref.current.setSort([]);OverflowMenuProvider.setVisible(null);setExpandedKeys({});},
                template: itemRenderer,
            },
            {
                key: '0_5',
                label: t('Common.SetMask'),
                icon: 'pi pi-server',
                command: () => {setMaskVisible(true);OverflowMenuProvider.setVisible(null);setExpandedKeys({});},
                template: itemRenderer,
            },
            {
                key: '0_5b',
                label: t('Common.ResetUserMask'),
                icon: 'pi pi-refresh',
                command: () => { resetMask(); OverflowMenuProvider.setVisible(null); setExpandedKeys({}); },
                template: itemRenderer,
            },
            {
              key: '0_6',
              label: t('Common.Notifications'),
              icon: 'pi pi-envelope',
              command: () => {openChosenNotificationPreferences('TrainingCompanyCertRecordDetail');OverflowMenuProvider.setVisible(null);},
              template: itemRenderer,
          },
        ]
      }
    ];

      function changeMarkings(value){
        const columnVisible = {...columnsVisible};
        for(let val in columnVisible) {
          if(columnVisible[val]!=="id" || columnVisible[val]!=="editor" || columnVisible[val]!=="end_of_validity"){
            columnVisible[val]=value;
            ref.current.showColumn(`${val}`);
          }
        };
        setColumnsVisible(columnVisible);
      }
    
      const reject = () => {
          toast?.current?.show({ severity: 'warn', summary: t('Common.Rejected'), detail: t('Common.Toast.RequestCancelled'), life: 3000 });
      }

    const acceptCertificate = () => {
        let rows_to_delete = [];
        rows_to_delete = ref.current.getSelectedData();
        rows_to_delete.forEach(element => {
          console.log(element);
          deleteRequest(`/api/company_cert_records/${element.id}`)
              .then((response) => {
                toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('Common.Toast.RecordDeletedDetail') });
                loadTrainingCertificates();
              }).catch((reason) => {
                  alert(reason);
                  toast?.current?.show({ severity: 'warn', summary: t('Common.Rejected'), detail: t('Common.Toast.DeleteFailedDetail'), life: 3000 });
              })
        });
    }

      const confirm = () => {
        confirmDialog({
            message: t('Common.ConfirmDeleteRecord'),
            header: t('Common.ConfirmDelete'),
            icon: 'pi pi-info-circle',
            acceptClassName: 'p-button-danger',
            acceptLabel: t('Common.No'),
            rejectLabel: t('Common.Yes'),
            accept: reject,
            reject: acceptCertificate
        });
    };


    return ( 
        <>
            <div>
            <div className='w-full h-10 flex flex-row bg-[#333]'> 
            <IntersectionObserverWrapper>
            <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="edit"><button className="disabled:opacity-25" disabled={editIcon} onClick={() => {getRecordFiles(selectedRecord?.id);setVisibleRecord(true);}}><EditIcon/></button></div>
            <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="delete"><button className="disabled:opacity-25" onClick={confirm} disabled={deleteIcon}><DeleteIcon/></button></div>
            <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center ' data-targetid="print"><button onClick={() => ref.current.print(false, true)}><PrintIcon/></button></div>      
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="vert"><VerticalLineIcon/></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="first"><button className="disabled:opacity-25" onClick={() => scrollToFirst()} disabled={scrollIcons}><BackToFirstIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="back"><button className="disabled:opacity-25" onClick={() => scrollUp()} disabled={scrollIcons}><BackIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="forward"><button className="disabled:opacity-25" onClick={() => scrollDown()} disabled={scrollIcons}><ForwardIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="last"><button className="disabled:opacity-25" onClick={() => scrollToLast()} disabled={scrollIcons}><ForwardToLastIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="vert"><VerticalLineIcon/></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="favorite"><button className="disabled:opacity-25" disabled onClick={() => openFavoriteDialog(t('TrainingCompanyCertRecordDetail.Text.Upravit_certifikaty_skol_firiem'),"TrainingCompanyCertRecordDetail")}><FavoriteIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="price"><button className="disabled:opacity-25" disabled onClick={() => setFastView("TrainingCompanyCertRecordDetail",columnsVisible,columns)}><PriceIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="vert"><VerticalLineIcon/></div>
          <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem"/>
          </div>
          </IntersectionObserverWrapper>
        </div>
        <Splitter className='h-[calc(100vh_-_177px)]' layout="vertical" pt={{ gutter: { className: 'bg-[#333] h-8' },gutterHandler: { className: 'bg-secondary ' } }}>
        <SplitterPanel className="flex align-items-center justify-content-center overflow-y-auto " minSize={10} size={63}>
        <div className="w-full relative flex flex-col h-full overflow-x">
          <div className='xl:grid grid-cols-2 gap-1 mb-2 '>
            <div className="w-auto p-6 m-4 bg-gray-100 rounded-md shadow-md">
                <h1 className="text-3xl font-semibold text-left text-zinc-900">{t('Ui.R67.PLATNOST_CERTIFIKATOV_SKOLIACEJ_FIRMY')}</h1>
                {formType=="edit" && <label className="block text-md font-semibold text-gray-800">Por.č.: {formTrainingCompany?.id}</label>}
                      <div>
                        <label
                            htmlFor="name"
                            className="block text-md font-semibold text-gray-800"
                        >{t('Common.CompanyNameUpper')}</label>
                        <input
                        readOnly={true}
                            name='name'
                            type="text"
                            value={formTrainingCompany?.name}
                            className="block w-full  px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                        />
                        </div>
                        <div>
                        <label
                            htmlFor="tel_number"
                            className="block text-md font-semibold text-gray-800"
                        >{t('Common.PhoneNumber')}</label>
                        <input
                        readOnly={true}
                            name='tel_number'
                            type="text"
                            value={formTrainingCompany?.tel_number}
                            className="block w-full px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                        />
                        </div>
                    
                      <div>
                        <label
                            htmlFor="email"
                            className="block text-md font-semibold text-gray-800"
                        >
                            E-MAIL
                        </label>
                        <input
                        readOnly={true}
                            name='email'
                            type="text"
                            value={formTrainingCompany?.email}
                            className="block w-full  px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                        />
                        </div>
                        <div>
                        <label
                            htmlFor="contact_person"
                            className="block text-md font-semibold text-gray-800 mt-2"
                        >{t('Common.ContactPersonUpper')}</label>
                        <input
                        readOnly={true}
                            name='contact_person'
                            type="text"
                            value={formTrainingCompany?.contact_person}
                            className="block w-full  px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                        />
                        </div>        
            </div>
            <div className="w-auto p-6 m-4 bg-gray-100 rounded-md shadow-md">
                    <div>
                        <label
                            htmlFor="address"
                            className="block text-md font-semibold text-gray-800"
                        >
                            ADRESA
                        </label>
                        <input
                        readOnly={true}
                            name='address'
                            type="text"
                            value={formTrainingCompany?.address}
                            className="block w-full  px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                        />
                        </div>
                        <div>
                        <label
                            htmlFor="fax"
                            className="block text-md font-semibold text-gray-800"
                        >
                             FAX
                        </label>
                        <input
                        readOnly={true}
                            name='fax'
                            type="text"
                            value={formTrainingCompany?.fax}
                            className="block w-full px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                        />
                        </div>
                        <div>
                        <label
                            htmlFor="web_address"
                            className="block text-md font-semibold text-gray-800"
                        >
                            WEB. ADRESA
                        </label>
                        <input
                        readOnly={true}
                            name='web_address'
                            type="text"
                            value={formTrainingCompany?.web_address}
                            className="block w-full  px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                        />
                        </div>
                        <div>
                        <label
                            htmlFor="description"
                            className="block text-md font-semibold text-gray-800"
                        >{t('Common.NoteUpper')}</label>
                        <textarea
                        readOnly={true}
                            name='gauge_name'
                            type="text"
                            value={formTrainingCompany?.description}
                            className="block w-full  px-4 py-2 mt-2 text-grey-700 bg-gray-200 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                        >
                        </textarea>
                        </div>    
            </div> 
            </div>
            </div> 
             </SplitterPanel>
             <SplitterPanel className="flex flex-col align-items-center justify-content-center w-full overflow-y-auto" size={37} style={{minHeight: '110px'}}>
             <ReactTabulator
               data={trainingCertificates} 
               className='h-[calc(100vh_-_176px)]' //176px
               onRef={(r) => (ref.current = r.current)}
               columns={columns}
               events={{
                 rowSelectionChanged: onSelectRow,
                 tableBuilt: () => {
                    let mask = state.user_mask_filters.filter(mask => mask.page=="TrainingCompanyCertRecordDetail");
                    if(mask.length>0){
                      let attributes = [];
                      try {
                        attributes = JSON.parse(mask[0].attributes)
                      } catch (e) {
                        attributes = []
                      }
                      if(attributes?.length>1)ref.current.setColumnLayout(appendAuditFieldsToColumnLayout(attributes));
                      let filters = [];
                      try {
                        filters = JSON.parse(mask[0]?.filters);
                      } catch (e) {
                        filters = [];
                      }
                      if(Array.isArray(filters)){
                        filters.forEach((f) => {
                          if(f?.field == null) return;
                          ref.current.setHeaderFilterValue?.(f.field, f.value);
                        });
                      }
                      let sorters = [];
                        try {
                          sorters = JSON.parse(mask[0]?.sorters);
                        } catch (e) {
                          sorters = []
                        }
                        if(sorters?.length>0)ref.current.setSort(sorters);
                      let json_columns;
                      try {
                        json_columns = JSON.parse(mask[0].columns);
                      } catch (e) {
                        json_columns = {};
                      }
                      const mergedColumns = mergeColumnVisibilityWithAuditDefaults(
                        TRAINING_COMPANY_CERT_RECORD_DETAIL_DEFAULT_COLUMNS_VISIBLE,
                        json_columns
                      );
                      setColumnsVisible(mergedColumns);
                      for(let column in mergedColumns){
                        if(mergedColumns[column]==false)ref.current.hideColumn(column);
                        else ref.current.showColumn(column);
                      }
                    }
                  },
                 columnMoved: () => { setUserMaskFilter("TrainingCompanyCertRecordDetail", "column_move", null, null, ref.current.getColumnLayout()); },
                 columnResized: () => { setUserMaskFilter("TrainingCompanyCertRecordDetail", "column_resized", null, null, ref.current.getColumnLayout()); }
               }}
               options={{renderHorizontal:"virtual",rowHeight:48,movableColumns: true,resizableColumnFit: true,
                dataTree:true,dataTreeStartExpanded:false,dataTreeFilter:false,dataTreeElementColumn: "name",
                dataTreeCollapseElement:ReactDOMServer.renderToStaticMarkup(<CollapseTreeIcon/>),
                dataTreeExpandElement:ReactDOMServer.renderToStaticMarkup(<ExpandTreeIcon/>),
                dataTreeBranchElement:ReactDOMServer.renderToStaticMarkup(<BranchTreeIcon/>),
                printAsHtml:true,printRowRange:"active",printCopyStyle:true,
                printHeader: () => buildCanonicalPrintHeaderHtml({
                  branding: state?.branding,
                  productFallbackSrc: defaultProductLogoSrc,
                  companySettingsPrint: state?.company_settings_print,
                  moduleSettings: state?.module_settings,
                  title: t('Print.Title.TrainingCompanyCertRecordDetail'),
                  printDateLabel: t('Print.DateLabel'),
                }),
                printFooter: () => buildCanonicalPrintFooterHtml({ companySettingsPrint: state?.company_settings_print }),
                initialFilter:[
                    {field:"company_id", type:"=", value:formTrainingCompany?.id}
                ],
                dataLoader: true,
                dataLoaderLoading: t('Common.LoadingData'),
                dataLoaderError: t('Common.LoadFailed'),
               }}/>
            </SplitterPanel>
            </Splitter>
            </div>
            <Dialog header={t('Common.EditCertificateRecord')} visible={visibleRecord} style={{ width: '40vw' }} onHide={() => hideCertRecord()}>
              <form onSubmit={formikUpdate.handleSubmit}>
                  <div>
                    <label
                        htmlFor="certificate_id"
                        className="block text-md font-semibold text-gray-800"
                    >{t('Ui.R67.Certifikat_kal_firmy')}</label>
                    <select 
                    disabled
                    name="certificate_id"
                    onChange={formikUpdate.handleChange} value={formikUpdate.values.certificate_id} 
                    className="bg-white block w-full  px-4 py-2 mt-2 text-grey-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40">
                      <option value="">{t('Ui.R67.Vyber_certifikat')}</option>
                      {trainingCertificates.map((certificate) => (
                        <option key={certificate.id} value={certificate.id}>{certificate.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                        htmlFor="start_of_validity"
                        className="block text-md font-semibold text-gray-800"
                    >{t('Ui.R67.Datum_platnosti_od')}</label>
                    <input
                        name='start_of_validity'
                        type="date" required
                        onChange={formikUpdate.handleChange} value={formikUpdate.values.start_of_validity}
                        className="block w-full  px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                    />
                    </div>
                    <div>
                    <label
                        htmlFor="end_of_validity"
                        className="block text-md font-semibold text-gray-800"
                    >{t('Ui.R67.Datum_platnosti_do')}</label>
                    <input
                        name='end_of_validity'
                        type="date" required
                        onChange={formikUpdate.handleChange} value={formikUpdate.values.end_of_validity}
                        className="block w-full  px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                    />
                    </div>
                    <div>
                    <label
                        htmlFor="description"
                        className="block text-md font-semibold text-gray-800"
                    >{t('Common.Note')}</label>
                    <textarea
                        name='description'
                        type="text"
                        onChange={formikUpdate.handleChange} value={formikUpdate.values.description}
                        className="block w-full  px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                    >
                    </textarea>
                    </div> 
                      <div>
                      <label
                          htmlFor="files"
                          className="block text-md font-semibold text-gray-800"
                      >
                          PROTOKOL
                      </label>
                      <input
                          name='files' 
                          accept='image/*,application/pdf'
                          ref={updateFileListRef}
                          onChange={handleUpdateFileChange}
                          className='block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40'
                          type='file'
                          multiple="multiple"
                          id='file'/>
                      </div> 
                      {images!=='' && images.map((img) => ( <div key={img.id} className='flex flex-row p-2'>
                          <div onClick={() => openFilePreview({ url: img.url, filename: img.filename })} className="cursor-pointer">
                              {/\.(pdf|png|jpg|jpeg|gif|webp|bmp|svg|img)$/i.test(img.filename || '') ? (
                                  <embed src={img.url+'#view=FitH&scrollbar=0'} width="100px" height="65px" />
                              ) : (
                                  <div className="w-[100px] h-[65px] flex items-center justify-center bg-gray-100 border rounded"><PaperClipIcon /></div>
                              )}
                          </div>
                              <div className='flex flex-col ml-4'>
                              <a style={{color:'blue',cursor:'pointer',marginBottom:'2px'}} onClick={() => openFilePreview({ url: img.url, filename: img.filename })}>{img.filename}</a>
                              <button type="button" className='w-fit flex flex-row px-2 py-1 border rounded-md bg-red-900' onClick={() => deleteFiles(img)} ><DeleteIcon/><span className='text-white ml-1'>{t('Common.Clear')}</span></button>
                              </div>
                          </div>))}
                      <button type="submit" className='flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600 disabled:opacity-25'>{t('Common.Save')}</button>         
                  </form>
              </Dialog>
      <Dialog header={t('Common.SetMask')} visible={maskVisible} style={{ width: '70vw' }} onHide={() => setMaskVisible(false)}>
        <div className="flex flex-col">
        <button type="button" onClick={() => {changeMarkings(!markUnmark);setMarkUnmarked(!markUnmark);}} className='m-1 px-4 py-2 text-center tracking-wide text-white transition-colors duration-200 transform bg-zinc-700 rounded-md hover:bg-zinc-500'>{markUnmark ? t('Common.DeselectAllUpper') : t('Common.SelectAllUpper')}</button>
        {columns.map(function(item) {
          if (!TRAINING_COMPANY_CERT_RECORD_DETAIL_MASK_DIALOG_FIELDS.includes(item.field)) return null;
                    return <div
                        key={item.field}
                        className="flex flex-row border border-black rounded-md p-2 m-1"
                    > 

                         <Checkbox onChange={(e) => setMask(item.field,e.checked)} checked={columnsVisible[item.field]}></Checkbox><span className="ml-2">{item.title}</span>
                          
                        {/* Use the React icon component */} 
                    </div> 
                })} 
            <button onClick={() => {setUserMaskFilter("TrainingCompanyCertRecordDetail","set_columns",columnsVisible,null,null);setMaskVisible(false);}} className='m-1 px-4 py-2 text-center tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600'>{t('Ui.R67.SaveUpper')}</button>
        </div>
      </Dialog>
        
            </> 
            );
} 
